import { Injectable } from "@angular/core";
import { Observable } from "rxjs/internal/Observable";
import { GlobalConstants } from "src/app/global-constants";
import { ReplaySubject, firstValueFrom } from "rxjs";
import { PlatformService } from "../platform/platform.service";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";

@Injectable({
  providedIn: "root",
})

/**
 * El servicio es responsable de cargar la configuración del períódico desde el backend y la configuración inicial crítica de GlobalConstants
 * y proporcionarla a otros servicios.
 */
export class ConfigService {
  // Constantes globales críticas
  private readonly globalConstants: any;
  private readonly API_URL: any;
  private readonly API_SHARED_URL: any;

  // ReplaySubject de la configuración del periódico del backend
  configPrensa$: ReplaySubject<any> = new ReplaySubject(1);

  constructor(
    private readonly platformService: PlatformService,
    private readonly httpClient: HttpClient,
  ) {
    this.globalConstants = new GlobalConstants(this.platformService.getLocationHostname());
    this.API_URL = this.globalConstants.urlApi;
    this.API_SHARED_URL = this.globalConstants.urlSharedApi;
  }

  /**
   * La función carga la configuración del periódico desde el backend y la almacena en un ReplaySubject para que otros servicios puedan suscribirse a ella.
   * @returns Promise<any> - La promesa se resuelve con la configuración del periódico cargada desde el backend.
   */
  loadConfigPrensa() {
    return new Promise((resolve, reject) => {
      const configPrensaApi = this.getConfigPrensaApi(this.globalConstants.idAmbito);
      if (configPrensaApi) {
        firstValueFrom(configPrensaApi)
          .then((config) => {
            this.configPrensa$.next(config);
            resolve(config);
          })
          .catch((error) => reject(error));
      }
    });
  }

  /**
   * La función devuelve la configuración del periódico almacenada.
   * @returns Observable<any> - Un observable que emite la configuración del periódico.
   */
  getConfigPrensa(): Observable<any> {
    return this.configPrensa$.asObservable();
  }

  /**
   * La función establece la configuración del periódico almacenada.
   * @param value - La configuración del periódico a almacenar.
   */
  public setConfigPrensa(value): void {
    this.configPrensa$.next(value);
  }

  /**
   * La función devuelve las constantes globales críticas.
   * @returns any - Las constantes globales críticas.
   */
  public getConstantesGlobales() {
    return this.globalConstants;
  }

  /**
   * Obtiene la configuración de la API de prensa para un ámbito específico.
   *
   * @param idAmbito - El identificador del ámbito para el cual se desea obtener la configuración.
   * @returns Un observable que emite la respuesta de la API.
   * @throws Error si no se ha definido el id del ámbito o la URL de la API.
   */
  public getConfigPrensaApi(idAmbito: number): Observable<any> {
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json, text/javascript, */*; q=0.01",
    });
    if (!idAmbito || !this.API_URL) {
      throw new Error("No se ha definido el id del ambito o la url de la api");
    }
    const body = new HttpParams().set("id_ambito", idAmbito);
    return this.httpClient.get(this.API_URL + "/getConfigPrensa", { headers: headers, params: body });
  }
}
